/**
 * Custom Data Store 모듈
 * @module shared/store/UseCustomDataStore
 * @author Andy
 */
import { create } from 'zustand';

/**
 * 관리자 또는 사용자 데이터 인터페이스
 * @author Andy
 */
interface CustomDataMap {
  [typeCode: string]: {
    [key: string]: string | object | any;
  };
}

/**
 * Custom Data Interface
 * @author Andy
 */
interface CustomDataInterface {
  customData: CustomData[];
  setCustomData: (paths: CustomData[]) => void;

  adminData: Record<string, any>;
  userData: Record<string, any>;
  setAdminData: (data: CustomDataMap) => void;
  setUserData: (data: CustomDataMap) => void;

  facilityLayer: FacilityLayer[];
  setFacilityLayer: (data: FacilityLayer[]) => void;

  userLayer: BaseLayerInterface[];
  setUserLayer: (data: BaseLayerInterface[]) => void;

  baseLayer: BaseLayerInterface[];
  setBaseLayer: (data: BaseLayerInterface[]) => void;

  getLayerInfo: (layerName: string) => object | undefined;

  terrainLayer: BaseLayerInterface[];
  setTerrainLayer: (data: BaseLayerInterface[]) => void;

  droneLayer: Record<string, any>[];
  setDroneLayer: (data: Record<string, any>[]) => void;

  customFunctions: CustomFunction[];
  setCustomFunctions: (funcs: CustomFunction[]) => void;
}

/**
 * CustomDataStore를 생성한다.
 * @author Andy
 */
const useCustomDataStore = create<CustomDataInterface>(set => ({
  customData: [],
  setCustomData: data => set({ customData: data }),

  adminData: {},
  userData: {},
  setAdminData: data => set({ adminData: data }),
  setUserData: data => set({ userData: data }),

  facilityLayer: [],
  setFacilityLayer: data => set({ facilityLayer: data }),

  userLayer: [],
  setUserLayer: data => set({ userLayer: data }),

  baseLayer: [],
  setBaseLayer: data => set({ baseLayer: data }),

  getLayerInfo: (layerName: string): object | undefined => {
    const state = useCustomDataStore.getState();
    const targetBaseLayer = state.baseLayer.find(
      (layer: BaseLayerInterface) => layer.name === layerName,
    );
    if (targetBaseLayer !== undefined && targetBaseLayer !== null) {
      return {
        isUserLayer: false,
        layerInfo: targetBaseLayer,
      };
    }

    return {
      isUserLayer: true,
      layerInfo: state.userLayer.find((layer: BaseLayerInterface) => layer.name === layerName),
    };
  },

  terrainLayer: [],
  setTerrainLayer: data => set({ terrainLayer: data }),

  droneLayer: [],
  setDroneLayer: data => set({ droneLayer: data }),

  customFunctions: [],
  setCustomFunctions: funcs => set({ customFunctions: funcs }),
}));

export default useCustomDataStore;
